/**
 * TV session: the tokens + room a TV holds after provisioning (pairing with its
 * LONG_LIVED setup code). Persisted in localStorage because a TV is left running —
 * a reload must NOT force re-setup. The access token is short-lived; the refresh
 * token re-mints it (pairing `POST /token/refresh`).
 *
 * Only the random device id is ever logged; tokens are persisted locally and sent
 * on backend calls, never embedded in source.
 */

import type { PairResponse } from '@spaces/contracts';
import { log } from '../log.js';
import type { PairingClient } from '../transport/pairing-client.js';

const STORAGE_KEY = 'spaces.tv.session';

export interface TvSession {
    accessToken: string;
    refreshToken?: string;
    expiresAt: string;
    meetingJwt: string;
    /** The room's 8x8 meeting-tenant slug (e.g. "emchodemo"); '' if unresolved. */
    meetingTenant: string;
    roomId: string;
    roomName: string;
}

export function sessionFromPair(pair: PairResponse): TvSession {
    return {
        accessToken: pair.accessToken,
        refreshToken: pair.refreshToken,
        expiresAt: pair.expiresAt,
        meetingJwt: pair.meetingJwt,
        meetingTenant: pair.meetingTenant,
        roomId: pair.room.id,
        roomName: pair.room.name
    };
}

export function loadSession(): TvSession | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);

        return raw ? (JSON.parse(raw) as TvSession) : null;
    } catch {
        return null;
    }
}

export function saveSession(session: TvSession): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
        log.warn('could not persist TV session (localStorage unavailable)');
    }
}

export function clearSession(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

/** True if the access token is expired or within 60s of expiring. */
export function accessTokenStale(session: TvSession): boolean {
    const expMs = Date.parse(session.expiresAt);

    return !Number.isFinite(expMs) || expMs - Date.now() < 60_000;
}

/** Decode a JWT's `exp` (ms epoch), or null if unparseable. */
function jwtExpMs(jwt: string): number | null {
    try {
        const seg = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const exp = (JSON.parse(atob(seg)) as { exp?: number }).exp;

        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
}

/**
 * True if the meetingJwt is expired or within 5 min of expiring (or unparseable).
 * The meetingJwt is what the TV presents to join 8x8.vc; it's 24h and minted at pair
 * time, so a long-running TV MUST re-mint it or the join eventually fails ("Token is
 * expired"). Refreshing it is folded into {@link ensureFreshAccess}.
 */
export function meetingJwtStale(session: TvSession): boolean {
    const expMs = jwtExpMs(session.meetingJwt);

    return expMs === null || expMs - Date.now() < 5 * 60_000;
}

/**
 * Ensure fresh tokens, refreshing via the refresh token if EITHER the access token or
 * the meetingJwt is stale. Returns the (possibly updated) session, or throws if it
 * cannot refresh. A TV that resumes with a stale meeting token (e.g. provisioned
 * before a signing-key change) self-heals here on the next provision/refresh.
 */
export async function ensureFreshAccess(session: TvSession, pairing: PairingClient): Promise<TvSession> {
    if (!accessTokenStale(session) && !meetingJwtStale(session)) {
        return session;
    }

    if (!session.refreshToken) {
        throw new Error('tokens stale and no refresh token available');
    }

    log.log('refreshing TV tokens');
    const refreshed = await pairing.refresh(session.refreshToken);
    const next: TvSession = {
        ...session,
        accessToken: refreshed.accessToken,
        expiresAt: refreshed.expiresAt,
        meetingJwt: refreshed.meetingJwt,
        // Pick up a slug that resolved after first pairing (operator logged in later).
        meetingTenant: refreshed.meetingTenant || session.meetingTenant
    };

    saveSession(next);

    return next;
}
